import * as React from 'react';

import {Button} from '../button/button';
import {SaveButton} from '../button/save-button/save-button';
import {StackLegacy} from '../layout/stack/stack-legacy';

type Props = {
    prepareViewButtonsDisabled: boolean;
    audienceSelectionViewButtonsDisabled: boolean;
    scheduleViewButtonsDisabled: boolean;
    saveButtonDisabled: boolean;
    currentView: string;
    setCurrentView: (arg1: string) => void;
    onSchedule?: () => void;
    setIsSending?: (arg1: boolean) => void;
    setIsShowingSendConfirmation: (arg1: boolean) => void;
    setSendEmailButtonRef: (arg1?: any) => void;
    // @ts-expect-error
    scheduledDate?: moment.Moment;
    recipientCount: number | null | undefined;
    isSending: boolean;
    isScheduling?: boolean;
    isAllowedToSchedule: boolean;
    setIsScheduling?: (arg1: boolean) => void;
    onCloseModal: () => void;
    getListSizeWarning?: () => React.ReactNode | null | undefined;
    shouldHideAudienceSelection?: boolean;
};

export function PrepareToSendButtons(props: Props) {
    const {
        prepareViewButtonsDisabled,
        audienceSelectionViewButtonsDisabled,
        scheduleViewButtonsDisabled,
        saveButtonDisabled,
        currentView,
        setCurrentView,
        onSchedule,
        setIsSending,
        setIsShowingSendConfirmation,
        setSendEmailButtonRef,
        scheduledDate,
        recipientCount,
        isSending,
        onCloseModal,
        isScheduling,
        setIsScheduling,
        shouldHideAudienceSelection,
    } = props;

    const views = getViews(isScheduling, shouldHideAudienceSelection);

    const handlePrevious = () => {
        const indexOfCurrentPage = views.indexOf(currentView);
        const newView = views[indexOfCurrentPage - 1];

        // @ts-expect-error
        return setCurrentView(newView);
    };

    const handleNext = () => {
        const indexOfCurrentPage = views.indexOf(currentView);
        const newView = views[indexOfCurrentPage + 1];

        // @ts-expect-error
        return setCurrentView(newView);
    };

    const isButtonDisabled = () => {
        let disabled = false;

        if (currentView === 'prepare' && prepareViewButtonsDisabled) {
            disabled = true;
        }

        if (currentView === 'recipients-selection' && audienceSelectionViewButtonsDisabled) {
            disabled = true;
        }

        if (currentView === 'schedule' && scheduleViewButtonsDisabled) {
            disabled = true;
        }

        return disabled;
    };

    const canSchedule = currentView === 'review' && !isScheduling && props.isAllowedToSchedule;
    const hasPrevious =
        currentView === 'recipients-selection' ||
        currentView === 'schedule' ||
        currentView === 'review';
    const hasNext =
        currentView === 'prepare' ||
        currentView === 'recipients-selection' ||
        currentView === 'schedule';
    const canSend = currentView === 'review';

    return (
        <StackLegacy spacing={16} isHorizontal={true} alignCenter={true}>
            {canSchedule && setIsScheduling ? (
                <Button
                    variant='text'
                    size='big'
                    // Disabled if we have a sending error
                    disabled={isButtonDisabled()}
                    onClick={() => {
                        setIsScheduling(true);
                        setCurrentView('schedule');
                    }}
                >
                    Schedule for a later date
                </Button>
            ) : undefined}
            {currentView === 'recipients-selection' && props.getListSizeWarning
                ? props.getListSizeWarning()
                : undefined}
            {hasPrevious ? (
                <Button variant='secondary' size='big' onClick={() => handlePrevious()}>
                    Previous
                </Button>
            ) : undefined}
            {hasNext ? (
                <Button
                    variant='primary'
                    size='big'
                    // Disabled if we have a sending error or the current view is incomplete
                    disabled={isButtonDisabled()}
                    onClick={() => handleNext()}
                >
                    Next
                </Button>
            ) : undefined}
            {canSend ? (
                <SaveButton
                    type='button'
                    // @ts-expect-error
                    onClick={() => {
                        if (isScheduling && onSchedule) {
                            if (setIsSending) {
                                // Schedule the email
                                setIsSending(true);
                            }

                            setIsShowingSendConfirmation(false);
                            onSchedule();
                        } else {
                            setIsShowingSendConfirmation(true);
                        }
                    }}
                    // @ts-expect-error
                    getButtonRef={(ref) => {
                        setSendEmailButtonRef(ref);
                    }}
                    // Disabled if anything needed to send is missing
                    disabled={saveButtonDisabled}
                    buttonText={
                        isScheduling && scheduledDate
                            ? `Schedule for ${scheduledDate.format('MMMM Do [at] h:mm A')}`
                            : `Send${
                                  recipientCount
                                      ? ` to ${recipientCount.toLocaleString()} ${
                                            recipientCount > 1 ? 'recipients' : 'recipient'
                                        }`
                                      : ''
                              }`
                    }
                    savingText={isScheduling ? 'Scheduling' : 'Sending'}
                    successText={isScheduling ? 'Scheduled!' : 'Sent!'}
                    onClearSaveComplete={() => {
                        onCloseModal();
                    }}
                    isSaving={isSending}
                    saveCompleteTimeout={1000}
                />
            ) : undefined}
        </StackLegacy>
    );

    function getViews(isScheduling?: boolean, shouldHideAudienceSelection?: boolean) {
        const views = ['prepare'];
        if (!shouldHideAudienceSelection) {
            views.push('recipients-selection');
        }
        if (isScheduling) {
            views.push('schedule');
        }
        views.push('review');
        return views;
    }
}
